import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindShow, bindText, bindValue, html, list } from "../lib/bind";
import { formatDate } from "../lib/date";
import { bindCheckbox, bindNullableSelect, bindNullableValue, bindSelect } from "../lib/formBind";
import { form } from "../lib/form";
import { addPasswordToggle } from "../lib/passwordField";
import { currentScope } from "../lib/scope";
import { labelRow } from "../lib/table";
import { toast } from "../lib/toast";
import { registerUnsavedGuard } from "../lib/unsavedGuard";
import { breadcrumb } from "../stores/breadcrumb";
import {
  type Company,
  type CompanyInput,
  archiveCompany,
  createCompany,
  getCompany,
  listCompanies,
  updateCompany,
  updateSmtp,
  uploadLogo,
} from "../services/company";

function emptyCompanyInput(): CompanyInput {
  return {
    name: "",
    trading_name: null,
    address_line1: null,
    address_line2: null,
    address_city: null,
    address_postcode: null,
    address_country: null,
    company_number: null,
    vat_number: null,
    vat_scheme: "flat_rate",
    frs_rate: null,
    frs_first_year_discount_until: null,
    frs_turnover_method: "cash",
    vat_stagger: null,
    default_currency: "GBP",
    default_vat_rate: "20.00",
    invoice_prefix: null,
    accounting_year_end: null,
    associated_companies_count: 0,
    bank_details_text: null,
    vat_outside_scope_note: null,
    email_from_name: null,
    email_from_address: null,
    email_bcc_self: false,
    email_provider_account_id: null,
  };
}

// accounting_year_end stores only month/day (year is a placeholder — a leap year, so
// Feb 29 round-trips) per app/models/company.py's own comment.
const ACCOUNTING_YEAR_END_PLACEHOLDER_YEAR = 2000;

function decomposeAccountingYearEnd(value: string | null): { month: string; day: string } {
  const match = /^\d{4}-(\d{2})-(\d{2})$/.exec(value ?? "");
  return { month: match?.[1] ?? "", day: match?.[2] ?? "" };
}

function composeAccountingYearEnd(month: string, day: string): string | null {
  if (!month || !day) return null;
  const dayNum = Number(day);
  if (!Number.isInteger(dayNum) || dayNum < 1) return null;
  const maxDay = new Date(ACCOUNTING_YEAR_END_PLACEHOLDER_YEAR, Number(month), 0).getDate();
  const clampedDay = Math.min(dayNum, maxDay);
  return `${ACCOUNTING_YEAR_END_PLACEHOLDER_YEAR}-${month}-${String(clampedDay).padStart(2, "0")}`;
}

function toInput(company: Company): CompanyInput {
  return {
    name: company.name,
    trading_name: company.trading_name,
    address_line1: company.address_line1,
    address_line2: company.address_line2,
    address_city: company.address_city,
    address_postcode: company.address_postcode,
    address_country: company.address_country,
    company_number: company.company_number,
    vat_number: company.vat_number,
    vat_scheme: company.vat_scheme,
    frs_rate: company.frs_rate,
    frs_first_year_discount_until: company.frs_first_year_discount_until,
    frs_turnover_method: company.frs_turnover_method,
    vat_stagger: company.vat_stagger,
    default_currency: company.default_currency,
    default_vat_rate: company.default_vat_rate,
    invoice_prefix: company.invoice_prefix,
    accounting_year_end: company.accounting_year_end,
    associated_companies_count: company.associated_companies_count,
    bank_details_text: company.bank_details_text,
    vat_outside_scope_note: company.vat_outside_scope_note,
    email_from_name: company.email_from_name,
    email_from_address: company.email_from_address,
    email_bcc_self: company.email_bcc_self,
    email_provider_account_id: company.email_provider_account_id,
  };
}

export function companiesListView(): Node {
  const companies = signal<Company[]>([]);
  const loadError = signal("");

  listCompanies()
    .then((result) => {
      companies.value = result;
    })
    .catch((err: unknown) => {
      loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
    });

  const fragment = html`<div class="view">
    <p class="form-error"></p>
    <div class="card">
      <div class="card-label-row">
        <a href="/companies/new" data-link class="btn btn-primary">Add company</a>
      </div>
      <div class="data-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>VAT scheme</th>
              <th>Currency</th>
            </tr>
          </thead>
          <tbody class="companies-body"></tbody>
        </table>
      </div>
    </div>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const table = fragment.querySelector<HTMLTableElement>(".data-table table");
  const tbody = fragment.querySelector<HTMLTableSectionElement>(".companies-body");
  if (!errorEl || !table || !tbody) throw new Error("companies list markup missing expected elements");
  bindErrorText(errorEl, loadError);

  list(
    tbody,
    companies,
    (c) => c.id,
    (c) => {
      const row = html`<tr>
        <td><a href="/companies/${c.id}" data-link>${c.name}</a></td>
        <td>${c.vat_scheme}</td>
        <td>${c.default_currency}</td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("company row template produced no element");
      return labelRow(row, table);
    },
  );

  return fragment;
}

export function companyDetailView(params: Record<string, string>): Node {
  const paramId = params.id ?? "";
  const isNew = paramId === "new";
  const companyId = signal(isNew ? "" : paramId);
  const savedAt = signal("");
  const errorMessage = signal("");

  breadcrumb.value = { label: "Companies", path: "/companies" };
  currentScope().cleanup(() => {
    breadcrumb.value = null;
  });

  const companyForm = form(emptyCompanyInput());
  const smtpPasswordForm = form({ smtpPassword: "" });
  // Unsaved-changes guard (Phase 2a item 11) — 04-frontend.md specs it for settings
  // forms; the SMTP password field counts (typed-but-untested credentials are edits).
  let leavingClean = false;
  registerUnsavedGuard(
    () => !leavingClean && (companyForm.dirty.value || smtpPasswordForm.dirty.value),
  );
  const smtpError = signal("");
  const logoPath = signal<string | null>(null);
  const smtpVerifiedAt = signal<string | null>(null);
  const smtpVerifiedLabel = computed(() =>
    smtpVerifiedAt.value ? `Last connected on ${formatDate(smtpVerifiedAt.value)}` : "",
  );
  const smtpVerified = computed(() => smtpVerifiedAt.value !== null);

  if (!isNew) {
    getCompany(paramId)
      .then((company) => {
        companyForm.reset(toInput(company));
        savedAt.value = company.updated_at;
        logoPath.value = company.logo_path;
        smtpVerifiedAt.value = company.smtp_verified_at;
      })
      .catch((err: unknown) => {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }

  async function submit(event: Event): Promise<void> {
    event.preventDefault();
    errorMessage.value = "";
    try {
      if (isNew) {
        const created = await createCompany(companyForm.values());
        toast.queueSuccess("Company created.");
        leavingClean = true;
        location.assign(`/companies/${created.id}`);
        return;
      }
      const updated = await updateCompany(companyId.value, {
        ...companyForm.values(),
        updated_at: savedAt.value,
      });
      savedAt.value = updated.updated_at;
      companyForm.reset(companyForm.values());
      toast.success("Company saved.");
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 412) {
        errorMessage.value = "This record changed elsewhere — reload and retry.";
      } else {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Save failed";
      }
      toast.error(errorMessage.value);
    }
  }

  async function archive(): Promise<void> {
    if (!window.confirm("Archive this company?")) return;
    try {
      await archiveCompany(companyId.value);
      toast.queueSuccess("Company archived.");
      leavingClean = true;
      location.assign("/companies");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Archive failed");
    }
  }

  async function submitLogo(event: Event): Promise<void> {
    event.preventDefault();
    const file = logoInput?.files?.[0];
    if (!file) return;
    try {
      const updated = await uploadLogo(companyId.value, file);
      logoPath.value = updated.logo_path;
      toast.success("Logo uploaded.");
    } catch (err) {
      errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Upload failed";
      toast.error(errorMessage.value);
    }
  }

  async function submitSmtp(event: Event): Promise<void> {
    event.preventDefault();
    smtpError.value = "";
    try {
      const updated = await updateSmtp(
        companyId.value,
        companyForm.fields.email_provider_account_id.value,
        smtpPasswordForm.fields.smtpPassword.value,
      );
      savedAt.value = updated.updated_at;
      smtpVerifiedAt.value = updated.smtp_verified_at;
      // Re-baseline only (field deliberately keeps its text — Phase 1b decision);
      // the successfully-saved password no longer counts as an unsaved edit.
      smtpPasswordForm.reset(smtpPasswordForm.values());
      toast.success("SMTP test email sent.");
    } catch (err) {
      smtpError.value = err instanceof ApiRequestError ? err.body.message : "SMTP test failed";
      toast.error(smtpError.value);
    }
  }

  const fragment = html`<div class="view">
    <div class="page-header">
      <h1>${isNew ? "New company" : "Company settings"}</h1>
    </div>
    <p class="form-error"></p>

    <form class="form company-form" onsubmit="${submit}">
      <div class="form-row">
        <div class="form-field">
          <label for="c-name">Name</label>
          <input id="c-name" required />
        </div>
        <div class="form-field">
          <label for="c-trading-name">Trading name</label>
          <input id="c-trading-name" />
        </div>
        <div class="form-field">
          <label for="c-company-number">Company number</label>
          <input id="c-company-number" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-field">
          <label for="c-address-line1">Address line 1</label>
          <input id="c-address-line1" />
        </div>
        <div class="form-field">
          <label for="c-address-line2">Address line 2</label>
          <input id="c-address-line2" />
        </div>
        <div class="form-field">
          <label for="c-address-city">City</label>
          <input id="c-address-city" />
        </div>
        <div class="form-field">
          <label for="c-address-postcode">Postcode</label>
          <input id="c-address-postcode" />
        </div>
        <div class="form-field">
          <label for="c-address-country">Country</label>
          <input id="c-address-country" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-field">
          <label for="c-vat-number">VAT number</label>
          <input id="c-vat-number" />
        </div>
        <div class="form-field">
          <label for="c-vat-scheme">VAT scheme</label>
          <select id="c-vat-scheme">
            <option value="flat_rate">Flat Rate Scheme</option>
            <option value="none">Not registered</option>
            <option value="standard_accrual">Standard (accrual)</option>
            <option value="cash_accounting">Cash accounting</option>
          </select>
        </div>
        <div class="form-field">
          <label for="c-frs-rate">FRS rate (%)</label>
          <input id="c-frs-rate" inputmode="decimal" />
        </div>
        <div class="form-field">
          <label for="c-default-vat-rate">Default VAT rate (%)</label>
          <input id="c-default-vat-rate" inputmode="decimal" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-field">
          <label for="c-vat-stagger">VAT stagger</label>
          <select id="c-vat-stagger">
            <option value="">—</option>
            <option value="mar_jun_sep_dec">Mar / Jun / Sep / Dec</option>
            <option value="apr_jul_oct_jan">Apr / Jul / Oct / Jan</option>
            <option value="may_aug_nov_feb">May / Aug / Nov / Feb</option>
          </select>
        </div>
        <div class="form-field">
          <label for="c-invoice-prefix">Invoice prefix</label>
          <input id="c-invoice-prefix" />
        </div>
        <div class="form-field">
          <label for="c-default-currency">Default currency</label>
          <input id="c-default-currency" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-field">
          <label for="c-frs-turnover-method">FRS turnover basis</label>
          <select id="c-frs-turnover-method">
            <option value="cash">Cash (payment date)</option>
            <option value="invoice">Invoice (issue date)</option>
          </select>
        </div>
        <div class="form-field">
          <label for="c-frs-first-year-discount-until">FRS first-year 1% discount until</label>
          <input id="c-frs-first-year-discount-until" type="date" />
          <p class="field-hint">Leave blank once the first-year discount no longer applies.</p>
        </div>
      </div>

      <div class="form-row">
        <div class="form-field">
          <label for="c-accounting-year-end-month">Financial year end (month)</label>
          <select id="c-accounting-year-end-month">
            <option value="">—</option>
            <option value="01">January</option>
            <option value="02">February</option>
            <option value="03">March</option>
            <option value="04">April</option>
            <option value="05">May</option>
            <option value="06">June</option>
            <option value="07">July</option>
            <option value="08">August</option>
            <option value="09">September</option>
            <option value="10">October</option>
            <option value="11">November</option>
            <option value="12">December</option>
          </select>
        </div>
        <div class="form-field">
          <label for="c-accounting-year-end-day">Day</label>
          <input id="c-accounting-year-end-day" type="number" min="1" max="31" />
        </div>
      </div>
      <p class="field-hint">
        Reports (P/L, aging, income by client) default to the accounting year this
        produces, so you don't need to re-enter a date range each time.
      </p>

      <div class="form-field">
        <label for="c-associated-companies">Associated companies</label>
        <input id="c-associated-companies" type="number" min="0" step="1" />
      </div>
      <p class="field-hint">
        Other companies under common control, not counting this one. The Corporation Tax
        thresholds are divided between them, so one associate halves them to £25,000 and
        £125,000 — which changes the estimate materially. Leave at 0 if there are none.
      </p>

      <div class="form-field">
        <label for="c-bank-details">Bank details (printed on invoices)</label>
        <textarea id="c-bank-details" rows="5"></textarea>
      </div>
      <div class="form-field">
        <label for="c-outside-scope-note">Outside-scope VAT note (printed on invoices)</label>
        <textarea id="c-outside-scope-note" rows="3"></textarea>
        <p class="field-hint">
          Added under the totals on invoices marked <strong>Outside scope of UK VAT</strong>,
          after the app's own "This supply is outside the scope of UK VAT." Use it for any
          reverse-charge wording your accountant specifies — whether one applies depends on
          where your customer is, so the app doesn't guess.
        </p>
      </div>

      <div class="form-row">
        <div class="form-field">
          <label for="c-email-from-name">Email from name</label>
          <input id="c-email-from-name" />
        </div>
        <div class="form-field">
          <label for="c-email-from-address">Email from address</label>
          <input id="c-email-from-address" type="email" />
          <p class="field-hint">The visible sender — can be a Fastmail alias, doesn't need to be a Fastmail login.</p>
        </div>
      </div>
      <div class="checkbox-field">
        <input id="c-email-bcc-self" type="checkbox" />
        <label for="c-email-bcc-self">BCC myself on sent invoices</label>
      </div>

      <div class="form-actions">
        <button class="btn btn-primary" type="submit">Save</button>
        <button class="btn btn-danger archive-btn" type="button" onclick="${archive}">
          Archive
        </button>
      </div>
    </form>

    <div class="card logo-card">
      <div class="card-label">Logo</div>
      <img class="logo-preview" alt="Company logo" />
      <form class="form" onsubmit="${submitLogo}">
        <input class="logo-input" type="file" accept="image/png,image/jpeg" />
        <div class="form-actions">
          <button class="btn" type="submit">Upload</button>
        </div>
      </form>
    </div>

    <div class="card smtp-card">
      <div class="card-label">SMTP (Fastmail app password)</div>
      <div class="smtp-status">
        <span class="smtp-status-dot"></span>
        <span class="smtp-status-text"></span>
      </div>
      <form class="form" onsubmit="${submitSmtp}">
        <div class="form-field">
          <label for="c-email-provider-account-id">Fastmail account (sign-in email)</label>
          <input id="c-email-provider-account-id" type="email" />
        </div>
        <div class="form-field">
          <label for="smtp-password">App password</label>
          <input id="smtp-password" type="password" autocomplete="off" required />
          <p class="field-hint">Not shown here, deliberately — re-enter it to change it.</p>
        </div>
        <p class="form-error smtp-error"></p>
        <div class="form-actions">
          <button class="btn" type="submit">Send test email &amp; save</button>
        </div>
      </form>
    </div>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const archiveBtn = fragment.querySelector<HTMLButtonElement>(".archive-btn");
  const logoInput = fragment.querySelector<HTMLInputElement>(".logo-input");
  const logoPreview = fragment.querySelector<HTMLImageElement>(".logo-preview");
  const smtpErrorEl = fragment.querySelector<HTMLParagraphElement>(".smtp-error");
  const smtpStatusEl = fragment.querySelector<HTMLElement>(".smtp-status");
  const smtpStatusTextEl = fragment.querySelector<HTMLElement>(".smtp-status-text");
  const nameInput = fragment.querySelector<HTMLInputElement>("#c-name");
  const tradingNameInput = fragment.querySelector<HTMLInputElement>("#c-trading-name");
  const companyNumberInput = fragment.querySelector<HTMLInputElement>("#c-company-number");
  const addressLine1Input = fragment.querySelector<HTMLInputElement>("#c-address-line1");
  const addressLine2Input = fragment.querySelector<HTMLInputElement>("#c-address-line2");
  const addressCityInput = fragment.querySelector<HTMLInputElement>("#c-address-city");
  const addressPostcodeInput = fragment.querySelector<HTMLInputElement>("#c-address-postcode");
  const addressCountryInput = fragment.querySelector<HTMLInputElement>("#c-address-country");
  const vatNumberInput = fragment.querySelector<HTMLInputElement>("#c-vat-number");
  const vatSchemeSelect = fragment.querySelector<HTMLSelectElement>("#c-vat-scheme");
  const frsRateInput = fragment.querySelector<HTMLInputElement>("#c-frs-rate");
  const defaultVatRateInput = fragment.querySelector<HTMLInputElement>("#c-default-vat-rate");
  const vatStaggerSelect = fragment.querySelector<HTMLSelectElement>("#c-vat-stagger");
  const frsTurnoverMethodSelect = fragment.querySelector<HTMLSelectElement>(
    "#c-frs-turnover-method",
  );
  const frsFirstYearDiscountUntilInput = fragment.querySelector<HTMLInputElement>(
    "#c-frs-first-year-discount-until",
  );
  const invoicePrefixInput = fragment.querySelector<HTMLInputElement>("#c-invoice-prefix");
  const defaultCurrencyInput = fragment.querySelector<HTMLInputElement>("#c-default-currency");
  const accountingYearEndMonthSelect = fragment.querySelector<HTMLSelectElement>(
    "#c-accounting-year-end-month",
  );
  const accountingYearEndDayInput = fragment.querySelector<HTMLInputElement>(
    "#c-accounting-year-end-day",
  );
  const associatedCompaniesInput = fragment.querySelector<HTMLInputElement>(
    "#c-associated-companies",
  );
  const bankDetailsInput = fragment.querySelector<HTMLTextAreaElement>("#c-bank-details");
  const outsideScopeNoteInput = fragment.querySelector<HTMLTextAreaElement>(
    "#c-outside-scope-note",
  );
  const emailFromNameInput = fragment.querySelector<HTMLInputElement>("#c-email-from-name");
  const emailFromAddressInput = fragment.querySelector<HTMLInputElement>("#c-email-from-address");
  const emailProviderAccountIdInput = fragment.querySelector<HTMLInputElement>(
    "#c-email-provider-account-id",
  );
  const bccInput = fragment.querySelector<HTMLInputElement>("#c-email-bcc-self");
  const smtpPasswordInput = fragment.querySelector<HTMLInputElement>("#smtp-password");

  if (
    !errorEl ||
    !logoInput ||
    !logoPreview ||
    !smtpErrorEl ||
    !smtpStatusEl ||
    !smtpStatusTextEl ||
    !nameInput ||
    !tradingNameInput ||
    !companyNumberInput ||
    !addressLine1Input ||
    !addressLine2Input ||
    !addressCityInput ||
    !addressPostcodeInput ||
    !addressCountryInput ||
    !vatNumberInput ||
    !vatSchemeSelect ||
    !frsRateInput ||
    !defaultVatRateInput ||
    !vatStaggerSelect ||
    !frsTurnoverMethodSelect ||
    !frsFirstYearDiscountUntilInput ||
    !invoicePrefixInput ||
    !associatedCompaniesInput ||
    !defaultCurrencyInput ||
    !accountingYearEndMonthSelect ||
    !accountingYearEndDayInput ||
    !bankDetailsInput ||
    !outsideScopeNoteInput ||
    !emailFromNameInput ||
    !emailFromAddressInput ||
    !emailProviderAccountIdInput ||
    !bccInput ||
    !smtpPasswordInput
  ) {
    throw new Error("company detail markup missing expected elements");
  }
  if (isNew && archiveBtn) archiveBtn.style.display = "none";

  bindErrorText(errorEl, errorMessage);
  bindErrorText(smtpErrorEl, smtpError);
  bindShow(smtpStatusEl, smtpVerified);
  bindText(smtpStatusTextEl, smtpVerifiedLabel);

  bindValue(nameInput, companyForm.fields.name);
  bindNullableValue(tradingNameInput, companyForm.fields.trading_name);
  bindNullableValue(companyNumberInput, companyForm.fields.company_number);
  bindNullableValue(addressLine1Input, companyForm.fields.address_line1);
  bindNullableValue(addressLine2Input, companyForm.fields.address_line2);
  bindNullableValue(addressCityInput, companyForm.fields.address_city);
  bindNullableValue(addressPostcodeInput, companyForm.fields.address_postcode);
  bindNullableValue(addressCountryInput, companyForm.fields.address_country);
  bindNullableValue(vatNumberInput, companyForm.fields.vat_number);
  bindSelect(vatSchemeSelect, companyForm.fields.vat_scheme);
  bindNullableValue(frsRateInput, companyForm.fields.frs_rate);
  bindValue(defaultVatRateInput, companyForm.fields.default_vat_rate);
  bindNullableSelect(vatStaggerSelect, companyForm.fields.vat_stagger);
  bindSelect(frsTurnoverMethodSelect, companyForm.fields.frs_turnover_method);
  bindNullableValue(frsFirstYearDiscountUntilInput, companyForm.fields.frs_first_year_discount_until);
  bindNullableValue(invoicePrefixInput, companyForm.fields.invoice_prefix);
  // Hand-bound rather than bindValue: the field is a number, and an empty or junk input
  // must land as 0 rather than NaN — the API rejects NaN and the message wouldn't say why.
  currentScope().effect(() => {
    associatedCompaniesInput.value = String(companyForm.fields.associated_companies_count.value);
  });
  associatedCompaniesInput.addEventListener("input", () => {
    const parsed = Number.parseInt(associatedCompaniesInput.value, 10);
    companyForm.fields.associated_companies_count.value =
      Number.isNaN(parsed) || parsed < 0 ? 0 : parsed;
  });
  bindValue(defaultCurrencyInput, companyForm.fields.default_currency);
  currentScope().effect(() => {
    const { month, day } = decomposeAccountingYearEnd(companyForm.fields.accounting_year_end.value);
    if (document.activeElement !== accountingYearEndMonthSelect) {
      accountingYearEndMonthSelect.value = month;
    }
    if (document.activeElement !== accountingYearEndDayInput) {
      accountingYearEndDayInput.value = day;
    }
  });
  const updateAccountingYearEnd = (): void => {
    companyForm.fields.accounting_year_end.value = composeAccountingYearEnd(
      accountingYearEndMonthSelect.value,
      accountingYearEndDayInput.value,
    );
  };
  accountingYearEndMonthSelect.addEventListener("change", updateAccountingYearEnd);
  accountingYearEndDayInput.addEventListener("input", updateAccountingYearEnd);
  bindNullableValue(bankDetailsInput, companyForm.fields.bank_details_text);
  bindNullableValue(outsideScopeNoteInput, companyForm.fields.vat_outside_scope_note);
  bindNullableValue(emailFromNameInput, companyForm.fields.email_from_name);
  bindNullableValue(emailFromAddressInput, companyForm.fields.email_from_address);
  bindNullableValue(emailProviderAccountIdInput, companyForm.fields.email_provider_account_id);

  bindCheckbox(bccInput, companyForm.fields.email_bcc_self);

  currentScope().effect(() => {
    if (logoPath.value) {
      logoPreview.src = `/api/companies/${companyId.value}/logo?v=${encodeURIComponent(logoPath.value)}`;
      logoPreview.style.display = "block";
    } else {
      logoPreview.removeAttribute("src");
      logoPreview.style.display = "none";
    }
  });

  bindValue(smtpPasswordInput, smtpPasswordForm.fields.smtpPassword);
  addPasswordToggle(smtpPasswordInput);

  return fragment;
}
